import {
  CompanySelectorProps,
  ContainerSelectorProps,
  Entity,
  FreightTypeSelectorProps,
  MeterSelectorProps,
  OperatingAreaSelectorProps,
  RegionSelectorProps,
  RouteGroupSelectorProps,
  RouteSelectorProps,
  ServiceTypeSelectorProps,
  SiteSelectorProps,
  WellSelectorProps
} from "@ots-frontend/ots-data";
import {useMemo} from "react";
import {SelectableEntityType} from "../EntityTypeSelect/EntityTypeSelect";
import {BaseEntityMultiSelectProps} from "../base-entity-multi-select";
import {SiteMultiSelect} from "../SiteSelect/SiteMultiSelect";
import {RouteMultiSelect} from "../RouteSelect/RouteMultiSelect";
import {CompanyMultiSelect} from "../CompanySelect/CompanyMultiSelect";
import {WellMultiSelect} from "../WellSelect/WellMultiSelect";
import {OperatingAreaMultiSelect} from "../OperatingAreaSelect/OperatingAreaMultiSelect";
import {RouteGroupMultiSelect} from "../RouteGroupSelect/RouteGroupMultiSelect";
import {MeterMultiSelect} from "../MeterSelect/MeterMultiSelect";
import {FreightTypeMultiSelect} from "../FreightTypeSelect/FreightTypeMultiSelect";
import {ServiceTypeMultiSelect} from "../ServiceTypeSelect/ServiceTypeMultiSelect";
import {RegionMultiSelect} from "../RegionSelect/RegionMultiSelect";
import {ContainerMultiSelect} from "../ContainerSelect/ContainerMultiSelect";

export type EntityMultiSelectProps = Omit<BaseEntityMultiSelectProps<Entity>, "entities" | "loading"> & {
  entityType?: SelectableEntityType | string
  regionId?: string
  meterSelectorProps?: MeterSelectorProps
  containerSelectorProps?: ContainerSelectorProps
  wellSelectorProps?: WellSelectorProps
  siteSelectorProps?: SiteSelectorProps
  routeSelectorProps?: RouteSelectorProps
  routeGroupSelectorProps?: RouteGroupSelectorProps
  operatingAreaSelectorProps?: OperatingAreaSelectorProps
  companySelectorProps?: CompanySelectorProps
  freightTypeSelectorProps?: FreightTypeSelectorProps
  serviceTypeSelectorProps?: ServiceTypeSelectorProps
  regionSelectorProps?: RegionSelectorProps
}

export const EntityMultiSelect = (props: EntityMultiSelectProps) => {
  const {
    entityType,
    regionId,
    meterSelectorProps,
    containerSelectorProps,
    wellSelectorProps,
    siteSelectorProps,
    routeSelectorProps,
    routeGroupSelectorProps,
    operatingAreaSelectorProps,
    companySelectorProps,
    freightTypeSelectorProps,
    serviceTypeSelectorProps,
    regionSelectorProps,
    ...rest
  } = props

  const meterProps = useMemo(() => {
    if (meterSelectorProps && regionId) {
      return {
        ...meterSelectorProps,
        regionId: meterSelectorProps.regionId ?? regionId,
      }
    } else if (meterSelectorProps) {
      return {
        ...meterSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, meterSelectorProps])

  const containerProps = useMemo(() => {
    if (containerSelectorProps && regionId) {
      return {
        ...containerSelectorProps,
        regionId: containerSelectorProps.regionId ?? regionId,
      }
    } else if (containerSelectorProps) {
      return {
        ...containerSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, containerSelectorProps])

  const wellProps = useMemo(() => {
    if (wellSelectorProps && regionId) {
      return {
        ...wellSelectorProps,
        regionId: wellSelectorProps.regionId ?? regionId,
      }
    } else if (wellSelectorProps) {
      return {
        ...wellSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, wellSelectorProps])

  const siteProps = useMemo(() => {
    if (siteSelectorProps && regionId) {
      return {
        ...siteSelectorProps,
        regionId: siteSelectorProps.regionId ?? regionId,
      }
    } else if (siteSelectorProps) {
      return {
        ...siteSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, siteSelectorProps])

  const routeProps = useMemo(() => {
    if (routeSelectorProps && regionId) {
      return {
        ...routeSelectorProps,
        regionId: routeSelectorProps.regionId ?? regionId,
      }
    } else if (routeSelectorProps) {
      return {
        ...routeSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, routeSelectorProps])

  const routeGroupProps = useMemo(() => {
    if (routeGroupSelectorProps && regionId) {
      return {
        ...routeGroupSelectorProps,
        regionId: routeGroupSelectorProps.regionId ?? regionId,
      }
    } else if (routeGroupSelectorProps) {
      return {
        ...routeGroupSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, routeGroupSelectorProps])

  const operatingAreaProps = useMemo(() => {
    if (operatingAreaSelectorProps && regionId) {
      return {
        ...operatingAreaSelectorProps,
        regionId: operatingAreaSelectorProps.regionId ?? regionId,
      }
    } else if (operatingAreaSelectorProps) {
      return {
        ...operatingAreaSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, operatingAreaSelectorProps])

  const companyProps = useMemo(() => {
    if (companySelectorProps && regionId) {
      return {
        ...companySelectorProps,
        regionId: companySelectorProps.regionId ?? regionId,
      }
    } else if (companySelectorProps) {
      return {
        ...companySelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, companySelectorProps])

  const freightTypeProps = useMemo(() => {
    if (freightTypeSelectorProps && regionId) {
      return {
        ...freightTypeSelectorProps,
        regionId: freightTypeSelectorProps.regionId ?? regionId,
      }
    } else if (freightTypeSelectorProps) {
      return {
        ...freightTypeSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, freightTypeSelectorProps])

  const serviceTypeProps = useMemo(() => {
    if (serviceTypeSelectorProps && regionId) {
      return {
        ...serviceTypeSelectorProps,
        regionId: serviceTypeSelectorProps.regionId ?? regionId,
      }
    } else if (serviceTypeSelectorProps) {
      return {
        ...serviceTypeSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, serviceTypeSelectorProps])

  const regionProps = useMemo(() => {
    if (regionSelectorProps && regionId) {
      return {
        ...regionSelectorProps,
        regionId: regionSelectorProps.regionId ?? regionId,
      }
    } else if (regionSelectorProps) {
      return {
        ...regionSelectorProps
      }
    } else {
      return {
        regionId: regionId,
      }
    }
  }, [regionId, regionSelectorProps])

  return useMemo(() => {
    if (entityType === 'METER') {
      return (
        <MeterMultiSelect
          {...rest}
          selector={meterProps}/>
      );
    } else if (entityType === 'CONTAINER') {
      return (
        <ContainerMultiSelect
          {...rest}
          selector={containerProps}/>
      );
    } else if (entityType === 'WELL') {
      return (
        <WellMultiSelect
          {...rest}
          selector={wellProps}/>
      );
    } else if (entityType === 'SITE') {
      return (
        <SiteMultiSelect
          {...rest}
          selector={siteProps}/>
      );
    } else if (entityType === 'ROUTE') {
      return (
        <RouteMultiSelect
          {...rest}
          selector={routeProps}/>
      );
    } else if (entityType === 'ROUTEGROUP') {
      return (
        <RouteGroupMultiSelect
          {...rest}
          selector={routeGroupProps}/>
      );
    } else if (entityType === 'OPERATINGAREA') {
      return (
        <OperatingAreaMultiSelect
          {...rest}
          selector={operatingAreaProps}/>
      );
    } else if (entityType === 'COMPANY') {
      return (
        <CompanyMultiSelect
          {...rest}
          selector={companyProps}/>
      );
    } else if (entityType === 'FREIGHTTYPE') {
      return (
        <FreightTypeMultiSelect
          {...rest}
          selector={freightTypeProps}/>
      );
    } else if (entityType === 'SERVICETYPE') {
      return (
        <ServiceTypeMultiSelect
          {...rest}
          selector={serviceTypeProps}/>
      );
    } else if (entityType === 'REGION') {
      return (
        <RegionMultiSelect
          {...rest}
          selector={regionProps}/>
      );
    } else {
      return <div/>
    }
  }, [
    entityType,
    companyProps,
    meterProps,
    containerProps,
    operatingAreaProps,
    routeProps,
    routeGroupProps,
    siteProps,
    wellProps,
    freightTypeProps,
    serviceTypeProps,
    regionProps,
    rest])
}
